import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { sectorHooks } from './hooks';
const columns = [
    {
        key: 'name',
        header: 'Name'
    }
];
const fields = [
    {
        kind: 'text',
        name: 'name',
        label: 'Sector name',
        required: true,
        maxLength: 200
    }
];
const createDefaults = {
    id: '',
    name: ''
};
// Read sections plus, per row, the inline-edit control. Mirrors the create
// form's single `name` field so the drawer edits the same shape.
function sectorDetailSections(s) {
    return [
        {
            rows: [
                {
                    label: 'Name',
                    value: s.name || '—',
                    editField: {
                        kind: 'text',
                        name: 'name',
                        label: 'Sector name',
                        required: true,
                        maxLength: 200
                    }
                }
            ]
        }
    ];
}
export function SectorsTab() {
    const tenantId = useTenantId();
    const { data, isLoading } = sectorHooks.useList({
        companyId: tenantId
    });
    const upsert = sectorHooks.useUpsert();
    const remove = sectorHooks.useDelete();
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    // Store only the selected id and DERIVE the record from `items`, so an open
    // drawer reflects the latest list after an inline-edit Save without an effect
    // that re-syncs state (that infinite-loops on fresh-but-equal references).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((s)=>s.id === detailId) ?? null : null;
    const [detailMode, setDetailMode] = useState('read');
    const [deleting, setDeleting] = useState(null);
    // Shared by the create modal and the drawer edit-in-place; the target id is
    // passed in (empty = create).
    const submitSector = async (values, id)=>{
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            name: values.name
        });
    };
    const detailEdit = detail ? {
        defaultValues: {
            id: detail.id,
            name: detail.name ?? ''
        },
        onSubmit: (v)=>submitSector(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setFormOpen(true),
                    "data-testid": Testids.deliveryOrg.sectorCreate,
                    children: "New Sector"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryOrg.sectorsTable,
                rowTestid: Testids.deliveryOrg.sectorRow,
                rowKey: (s)=>s.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryOrg.sectorEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryOrg.sectorDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Sector",
                fields: fields,
                defaultValues: createDefaults,
                onSubmit: async (v)=>{
                    await submitSector(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.name || 'Sector' : '',
                sections: detail ? sectorDetailSections(detail) : [],
                testid: Testids.deliveryOrg.sectorDetailDrawer,
                edit: detailEdit,
                canEdit: true,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Sector",
                description: deleting ? `Permanently delete "${deleting.name}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
